import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { MessageSquare, Send } from 'lucide-react';
import { Textarea } from '@components/common';
import { noteService } from '@services';
import { NoteEntityType, ApiError } from '@app-types';
import { utcToLocal } from '@utils/dateUtils';
import Button from './Button';
import Tabs, { TabItem } from './Tabs';
import EntityHistory from './EntityHistory';
import styles from './EntityActivity.module.css';

interface EntityActivityProps {
  entityType: NoteEntityType;
  entityId: number;
  title?: string;
  /** Hides the create-note form for a viewer who may read Notes but not write them */
  canAddNote?: boolean;
}

const EntityActivity: React.FC<EntityActivityProps> = ({
  entityType,
  entityId,
  title = 'Activity',
  canAddNote = true
}) => {
  const [noteContent, setNoteContent] = useState('');
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState<'notes' | 'history'>('notes');

  const queryClient = useQueryClient();

  // Fetch notes
  const { data: notesData, isLoading } = useQuery({
    queryKey: ['notes', entityType, entityId],
    queryFn: () => noteService.getNotes(entityType, entityId),
    enabled: !!entityId,
    staleTime: 30000 // Cache for 30 seconds
  });

  // Create note mutation
  const createNoteMutation = useMutation({
    mutationFn: (content: string) => noteService.createNote(entityType, entityId, { content }),
    onSuccess: () => {
      // Refetch notes after creating a new one; the backend also writes an audit
      // entry for the note, so the History tab must be invalidated as well
      queryClient.invalidateQueries({ queryKey: ['notes', entityType, entityId] });
      queryClient.invalidateQueries({ queryKey: ['audit-history', entityType, entityId] });
      setNoteContent('');
      setError('');
    },
    onError: (error: ApiError) => {
      // Handle different error response structures
      const errorMessage =
        error?.response?.data?.error || error?.response?.data?.message || error?.message || 'Failed to create note';
      setError(String(errorMessage));
    }
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!noteContent.trim()) {
      setError('Note content cannot be empty');
      return;
    }

    createNoteMutation.mutate(noteContent.trim());
  };

  const formatDate = (dateString: string) => {
    const date = utcToLocal(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const tabs: TabItem[] = [
    { key: 'notes', label: 'Notes' },
    { key: 'history', label: 'History' }
  ];

  // Render notes list content based on loading/data state
  const renderNotesList = () => {
    if (isLoading) {
      return <div className={styles.notesLoading}>Loading notes...</div>;
    }

    if (notesData && notesData.notes.length > 0) {
      return (
        <div className={styles.notesListItems}>
          {notesData.notes.map((note) => (
            <div key={note.id} className={styles.noteItem}>
              <div className={styles.noteItemMeta}>
                <div className={styles.noteItemAuthor}>{note.authorName}</div>
                <div className={styles.noteItemDate}>{formatDate(note.createdAt)}</div>
              </div>
              <div className={styles.noteItemContent}>{note.content}</div>
            </div>
          ))}
        </div>
      );
    }

    return (
      <div className={styles.notesEmpty}>
        <MessageSquare size={48} className={styles.notesEmptyIcon} />
        <p>No notes yet</p>
        {canAddNote && <p className={styles.notesEmptySubtitle}>Add the first note above</p>}
      </div>
    );
  };

  // Render notes tab content
  const renderNotesTab = () => (
    <>
      {canAddNote && (
        <form onSubmit={handleSubmit} className={styles.noteCreateForm}>
          <Textarea
            value={noteContent}
            onChange={(e) => {
              setNoteContent(e.target.value);
              if (error) setError('');
            }}
            placeholder="Add a note..."
            rows={3}
            disabled={createNoteMutation.isPending}
            error={error}
            dataTestId="activity-note"
          />
          <div className={styles.noteCreateActions}>
            <Button
              type="submit"
              variant="primary"
              size="sm"
              loading={createNoteMutation.isPending}
              loadingText="Posting..."
              disabled={!noteContent.trim() || createNoteMutation.isPending}
            >
              <Send size={16} />
              <span>Post Note</span>
            </Button>
          </div>
        </form>
      )}

      {/* Notes list */}
      <div className={styles.notesList} data-test-id="notes-list">
        {renderNotesList()}
      </div>
    </>
  );

  return (
    <div className={styles.notesSection} data-test-id="notes-section">
      <div className={styles.notesHeader}>
        <div className={styles.notesHeaderTitle}>
          <MessageSquare size={20} />
          <h3>{title}</h3>
        </div>
        <div className={styles.notesHeaderTabs}>
          <Tabs items={tabs} activeTab={activeTab} onTabChange={(key) => setActiveTab(key as 'notes' | 'history')} />
        </div>
      </div>

      <div className={styles.notesContent}>
        {activeTab === 'notes' ? renderNotesTab() : <EntityHistory entityType={entityType} entityId={entityId} />}
      </div>
    </div>
  );
};

export default EntityActivity;
